/**
 * BrowserPanel - Live browser view for agentic credential provisioning
 *
 * Connects to SSE screenshot stream from browser-tools MCP server.
 * Shows the browser page the agent is controlling, allowing the user to
 * see login pages and click "I've logged in" to advance the workflow.
 */

import { useEffect, useRef, useState } from 'react';
import { cpFetch, CP_BASE } from '../../lib/cpClient';
import { Globe, Lock, Loader2, CheckCircle2, X } from 'lucide-react';

interface BrowserPanelProps {
  sessionId: string;
  screenshotStreamUrl?: string;
  onUserDone?: () => void;
  onClose?: () => void;
}

export default function BrowserPanel({ sessionId, screenshotStreamUrl, onUserDone, onClose }: BrowserPanelProps) {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [pageUrl, setPageUrl] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [connected, setConnected] = useState(false);
  const [signaling, setSignaling] = useState(false);
  const [done, setDone] = useState(false);

  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    const authData = localStorage.getItem('dc.auth');
    let token = '';
    if (authData) {
      try { token = JSON.parse(authData).token || ''; } catch { /* ignore */ }
    }

    const streamUrl = screenshotStreamUrl
      ? `${CP_BASE}${screenshotStreamUrl}${token ? `?token=${token}` : ''}`
      : `${CP_BASE}/api/volcano/tools/browser/stream/${sessionId}${token ? `?token=${token}` : ''}`;

    const es = new EventSource(streamUrl);

    es.onopen = () => setConnected(true);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.screenshot_base64) {
          setScreenshot(data.screenshot_base64);
        }
        if (data.url) setPageUrl(data.url);
        if (data.title) setPageTitle(data.title);
      } catch { /* ignore */ }
    };

    es.addEventListener('connected', () => setConnected(true));
    es.addEventListener('closed', () => {
      setConnected(false);
      setDone(true);
    });

    es.onerror = () => {
      setConnected(false);
    };

    esRef.current = es;

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [sessionId, screenshotStreamUrl]);

  async function handleUserDone() {
    if (signaling) return;
    setSignaling(true);

    try {
      // Signal the browser-tools server that user is done (login complete)
      await cpFetch('/api/volcano/tools/browser/signal_user_done', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
      setDone(true);
      onUserDone?.();
    } catch (err) {
      console.error('Failed to signal user done:', err);
    } finally {
      setSignaling(false);
    }
  }

  if (done) {
    return (
      <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">Login Complete</h3>
          <p className="text-sm text-gray-500 mt-2">
            The agent is now setting up your credentials...
          </p>
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin mt-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header with URL bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1.5">
          <Globe className={`w-4 h-4 ${connected ? 'text-green-500' : 'text-gray-400'}`} />
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        </div>

        {/* URL bar */}
        <div className="flex-1 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-xs text-gray-600 overflow-hidden">
          <Lock className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="truncate">{pageUrl || 'Loading...'}</span>
        </div>

        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Page title */}
      {pageTitle && (
        <div className="px-3 py-1 text-xs text-gray-500 border-b border-gray-100 truncate bg-gray-50/50">
          {pageTitle}
        </div>
      )}

      {/* Browser viewport */}
      <div className="flex-1 relative overflow-hidden bg-gray-100">
        {screenshot ? (
          <img
            src={`data:image/jpeg;base64,${screenshot}`}
            alt="Browser view"
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Connecting to browser...</span>
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Log in to the developer portal above.</span>
          <br />
          <span className="text-xs text-gray-500">The agent will take over after you log in.</span>
        </div>
        <button
          onClick={handleUserDone}
          disabled={signaling}
          className="px-5 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors shadow-sm"
        >
          {signaling ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Signaling...
            </span>
          ) : (
            "I've logged in"
          )}
        </button>
      </div>
    </div>
  );
}
